/**
 * How a full-screen layer comes and goes (`D193`): Material 3's shared axis,
 * on `transform` and `opacity` only, driven by Base UI's
 * `data-starting-style` / `data-ending-style`.
 *
 * - `PUSH_SURFACE` — forward navigation into a screen-sized surface (the
 *   profile, the body assessment, the retrospective, the swap picker): in
 *   from the right over 300 ms, emphasized-decelerate; out the same way over
 *   200 ms, emphasized-accelerate. The screen under it moves a little left
 *   and back (`underlay.ts`).
 * - `RISE_SURFACE` — a surface that comes up from below and is closed with a
 *   chevron down (the trainer and search on a phone): the same timings on
 *   the vertical axis.
 *
 * The transforms are spelled as `[transform:…]` on purpose. Tailwind 4's
 * `translate-x-*` and `scale-*` write the INDIVIDUAL `translate` and `scale`
 * properties, which `transition-[opacity,transform]` does not animate — that
 * is why the old `data-starting-style:translate-x-6` faded but never slid —
 * and the predictive Back owns `translate` and `scale` for its own preview
 * (`app/predictive-back.ts`), so the two compose instead of fighting.
 *
 * The entering layer takes taps from its first frame. A leaving one is
 * `inert` (the layer root's `popupProps`) and, for the length of the
 * ending style, `pointer-events: none` (index.css), so a tap during the
 * slide lands on what is underneath, never on what is going.
 */

const MOTION_GUARDS =
  "motion-reduce:transition-none [:root[data-shot]_&]:transition-none";

export const PUSH_SURFACE = [
  "transition-[opacity,transform] duration-300 ease-[cubic-bezier(0.05,0.7,0.1,1)]",
  "data-starting-style:[transform:translateX(8%)] data-starting-style:opacity-0",
  "data-ending-style:[transform:translateX(8%)] data-ending-style:opacity-0",
  "data-ending-style:duration-200 data-ending-style:ease-[cubic-bezier(0.3,0,0.8,0.15)]",
  MOTION_GUARDS,
].join(" ");

export const RISE_SURFACE = [
  "transition-[opacity,transform] duration-300 ease-[cubic-bezier(0.05,0.7,0.1,1)]",
  "data-starting-style:[transform:translateY(6%)] data-starting-style:opacity-0",
  "data-ending-style:[transform:translateY(6%)] data-ending-style:opacity-0",
  "data-ending-style:duration-200 data-ending-style:ease-[cubic-bezier(0.3,0,0.8,0.15)]",
  MOTION_GUARDS,
].join(" ");

/** A plain cross-fade, for a layer with no direction (the retrospective). */
export const FADE_SURFACE = [
  "transition-opacity duration-300 ease-[cubic-bezier(0.05,0.7,0.1,1)]",
  "data-starting-style:opacity-0 data-ending-style:opacity-0",
  "data-ending-style:duration-200 data-ending-style:ease-[cubic-bezier(0.3,0,0.8,0.15)]",
  MOTION_GUARDS,
].join(" ");
